import ModalController from "../../utils/ModalController";
import { initSectionThemeTriggers } from "../../utils/sectionThemeTriggers";
import { mountTurnstile, unmountTurnstile, waitForTurnstile, resetTurnstile } from "../../utils/turnstile";
import { flushQueuedEvents } from "../../utils/data-layer";

// ASCII-only, mirrors WordPress is_email(): accents and other special
// characters are rejected client-side instead of being stripped server-side.
const EMAIL_RE = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;

export default class NewsletterModalPush {
  constructor(el) {
    this.el = el;
    this.trigger = el.querySelector("[data-modal-trigger]");
    this.push = el.querySelector(".newsletter-modal-push");
    this.modal = el.querySelector(".c-newsletter-modal");
    initSectionThemeTriggers(this.modal);
    this.backdrop = this.modal.querySelector(".c-newsletter-modal__backdrop");
    this.closeBtn = this.modal.querySelector(".c-newsletter-modal__close");

    this.form = this.modal.querySelector("[data-newsletter-form]");
    this.input = this.form?.querySelector('input[name="email"]');
    this.consent = this.form?.querySelector('input[name="opt_in"]');
    this.submitCta = this.form?.querySelector(".c-cta");
    this.feedback = this.modal.querySelector("[data-newsletter-feedback]");

    this.ajaxUrl = this.form?.dataset.ajaxUrl || window.vsAssets?.ajaxUrl;
    this.nonce = this.form?.dataset.nonce || "";
    this.submitting = false;

    document.body.appendChild(this.modal);

    this.controller = new ModalController({
      modal: this.modal,
      backdrop: this.backdrop,
      closeBtn: this.closeBtn,
      onOpen: () => {
        this.push?.classList.add("is-hidden");
        mountTurnstile(this.form);
      },
      onClose: () => {
        this.push?.classList.remove("is-hidden");
        unmountTurnstile(this.form);
        this.resetState();
      },
    });

    this.init();
  }

  init() {
    this.trigger?.addEventListener("click", (e) => {
      e.preventDefault();
      this.controller.open();
    });

    this.form?.addEventListener("submit", (e) => this.handleSubmit(e));

    // Pauses the ripple animation while off screen (see _newsletter-modal-push.scss).
    if (this.push) {
      this.push.classList.add("is-offscreen");
      new IntersectionObserver(([entry]) => {
        this.push.classList.toggle("is-offscreen", !entry.isIntersecting);
      }).observe(this.push);
    }

    this.input?.addEventListener("input", () => this.clearFeedback());
    this.consent?.addEventListener("change", () => this.clearFeedback());
  }

  clearFeedback() {
    if (!this.feedback) return;
    this.feedback.textContent = "";
    this.modal.classList.remove("is-error", "is-success", "is-consent-error");
  }

  // Bring the form back (e.g. after a successful signup) when the modal reopens.
  resetState() {
    this.clearFeedback();
    this.setLoading(false);
    this.form?.reset();
  }

  setFeedback(message, type) {
    if (this.feedback) this.feedback.textContent = message;
    this.modal.classList.toggle("is-error", type === "error");
    this.modal.classList.toggle("is-success", type === "success");
  }

  setLoading(loading) {
    this.submitting = loading;
    this.form?.classList.toggle("is-loading", loading);
    this.submitCta?.classList.toggle("is-loading", loading);
    if (this.input) this.input.disabled = loading;
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (this.submitting) return;

    const email = (this.input?.value || "").trim();

    if (!EMAIL_RE.test(email)) {
      this.setFeedback("Please enter a valid email address.", "error");
      this.input?.focus();
      return;
    }

    if (this.consent && !this.consent.checked) {
      this.setFeedback("Please accept the terms to continue.", "error");
      this.modal.classList.add("is-consent-error");
      return;
    }

    if (!this.ajaxUrl) {
      this.setFeedback("Something went wrong. Please try again in a moment.", "error");
      return;
    }

    this.clearFeedback();
    this.setLoading(true);

    const turnstileToken = await waitForTurnstile(this.form);

    const body = new FormData();
    body.append("action", "addContactBrevo");
    body.append("security", this.nonce);
    body.append("email", email);
    if (this.consent) body.append("opt_in", this.consent.checked ? "1" : "0");
    if (turnstileToken) body.append("cf-turnstile-response", turnstileToken);

    fetch(this.ajaxUrl, { method: "POST", body })
      .then((res) => res.json())
      .then((data) => {
        this.setLoading(false);
        resetTurnstile(this.form);

        if (data && data.success) {
          flushQueuedEvents();
          this.setFeedback(
            data.data?.message || "You're in the loop.",
            "success",
          );
          this.form.reset();
        } else {
          this.setFeedback(
            data?.data?.message || "Something went wrong. Please try again in a moment.",
            "error",
          );
        }
      })
      .catch(() => {
        this.setLoading(false);
        resetTurnstile(this.form);
        this.setFeedback("Network error. Please check your connection and try again.", "error");
      });
  }
}
